import { useState, useEffect } from "react";
import api from "../../../../../../api/api";
import { chargeRowDefault } from "../constants";

const useVendorState = (initialVendors, containerOptions) => {
  const [vendors, setVendors] = useState([]);
  const [vendorInvoiceDates, setVendorInvoiceDates] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialVendors.length > 0) {
      setVendors(initialVendors);
    }
  }, [initialVendors]);

  const getEmptyVendorBlock = () => ({
    id: Date.now(),
    vendor_id: null,
    vendor_name: "",
    vendor_location: "",
    gst_number: "",
    pan_number: "",
    voucher_invoice_number: "",
    voucher_invoice_date: "",
    vb_number: "",
    vendor_job_number: "",
    shipper: "",
    consignee: "",
    mbl_number: "",
    vessel_name: "",
    vessel_number: "",
    voyage_number: "",
    etd_date: "",
    cut_off_date: "",
    inputChargeRow: { ...chargeRowDefault },
    chargeRows: [],
    items: [],
  });

  const addVendor = () => {
    setVendors((prev) => [...prev, getEmptyVendorBlock()]);
  };

  const removeVendor = (vendorId) => {
    setVendors((prev) => prev.filter((v) => v.id !== vendorId));
  };

  const updateVendor = (vendorId, field, value) => {
    setVendors((prev) =>
      prev.map((v) => (v.id === vendorId ? { ...v, [field]: value } : v)),
    );
  };

  const updateVendorInputCharge = (vendorId, field, value) => {
    setVendors((prev) =>
      prev.map((v) => {
        if (v.id !== vendorId) return v;
        const updatedInput = { ...v.inputChargeRow, [field]: value };
        if (field === "gst_applicable" && value === "No") {
          updatedInput.gst = "0";
          updatedInput.gst_amount = "0";
        }
        if (field === "rate" || field === "quantity") {
          const rate =
            field === "rate" ? Number(value) : Number(updatedInput.rate || 0);
          const quantity =
            field === "quantity"
              ? Number(value)
              : Number(updatedInput.quantity || 0);
          updatedInput.taxable_amount = (rate * quantity).toString();
        }
        if (
          field === "rate" ||
          field === "quantity" ||
          field === "exchange_rate" ||
          field === "currency"
        ) {
          const taxable = Number(updatedInput.taxable_amount) || 0;
          const exchange = Number(updatedInput.exchange_rate) || 1;
          updatedInput.amount_in_inr = Number((taxable * exchange).toFixed(2));
        }
        if (
          field === "rate" ||
          field === "quantity" ||
          field === "gst_applicable"
        ) {
          const gstPercent = Number(updatedInput.gst) || 0;
          const taxable = Number(updatedInput.taxable_amount) || 0;
          if (updatedInput.gst_applicable === "No" || gstPercent === 0) {
            updatedInput.gst_amount = "0";
          } else {
            updatedInput.gst_amount = Number(
              ((taxable * gstPercent) / 100).toFixed(2),
            );
          }
        }
        return { ...v, inputChargeRow: updatedInput };
      }),
    );
  };

  const handleVendorChargeInputChange = (vendorId, index, field, value) => {
    setVendors((prev) =>
      prev.map((v) => {
        if (v.id !== vendorId) return v;
        const updated = [...v.chargeRows];
        updated[index] = { ...updated[index], [field]: value };
        if (field === "gst_applicable" && value === "No") {
          updated[index].gst = "0";
          updated[index].gst_amount = "0";
        }
        if (field === "rate" || field === "quantity") {
          const rate =
            field === "rate" ? Number(value) : Number(updated[index].rate || 0);
          const quantity =
            field === "quantity"
              ? Number(value)
              : Number(updated[index].quantity || 0);
          updated[index].taxable_amount = (rate * quantity).toString();
        }
        if (
          field === "rate" ||
          field === "quantity" ||
          field === "exchange_rate" ||
          field === "currency"
        ) {
          const taxable = Number(updated[index].taxable_amount) || 0;
          const exchange = Number(updated[index].exchange_rate) || 1;
          updated[index].amount_in_inr = Number(
            (taxable * exchange).toFixed(2),
          );
        }
        return { ...v, chargeRows: updated };
      }),
    );
  };

  const addChargeRow = (vendorId) => {
    setVendors((prev) =>
      prev.map((v) => {
        if (v.id !== vendorId) return v;
        const { inputChargeRow } = v;
        if (!inputChargeRow.charge_code || !inputChargeRow.currency) {
          setErrors((prevErr) => ({
            ...prevErr,
            chargeTable: `Vendor ${prev.findIndex((x) => x.id === vendorId) + 1
              }: Please fill Charge Code and Currency before adding a new row`,
          }));
          return v;
        }
        setErrors((prevErr) => ({ ...prevErr, chargeTable: "" }));
        return {
          ...v,
          chargeRows: [...v.chargeRows, { ...inputChargeRow }],
          inputChargeRow: { ...chargeRowDefault },
        };
      }),
    );
  };

  const deleteChargeRow = (vendorIndex, index) => {
    setVendors((prev) => {
      const updated = [...prev];
      updated[vendorIndex].chargeRows = updated[vendorIndex].chargeRows.filter(
        (_, i) => i !== index,
      );
      return updated;
    });
  };

  const handleVendorSelect = (selectedVendor, vendorId) => {
    if (!selectedVendor) {
      updateVendor(vendorId, "vendor_id", null);
      updateVendor(vendorId, "vendor_name", "");
      updateVendor(vendorId, "vendor_location", "");
      updateVendor(vendorId, "gst_number", "");
      updateVendor(vendorId, "pan_number", "");
      return;
    }
    updateVendor(vendorId, "vendor_id", selectedVendor.vendor_id);
    updateVendor(vendorId, "vendor_name", selectedVendor.name || "");
    updateVendor(vendorId, "vendor_location", selectedVendor.location || "");
    updateVendor(vendorId, "gst_number", selectedVendor.gst_number || "");
    updateVendor(vendorId, "pan_number", selectedVendor.pan_number || "");
  };

  const handleCurrencyChange = async (vendorId, index, selectedCurrency) => {
    const country_code = selectedCurrency?.value || "";
    if (index === "input") {
      updateVendorInputCharge(vendorId, "currency", country_code);
    } else {
      handleVendorChargeInputChange(vendorId, index, "currency", country_code);
    }
    if (!country_code) {
      if (index === "input") {
        updateVendorInputCharge(vendorId, "exchange_rate", "");
      } else {
        handleVendorChargeInputChange(vendorId, index, "exchange_rate", "");
      }
      return;
    }
    try {
      const res = await api.get(`/exchange_rate/rate/${country_code}`);
      if (index === "input") {
        updateVendorInputCharge(vendorId, "exchange_rate", Number(res.data.rate_in_INR));
      } else {
        handleVendorChargeInputChange(vendorId, index, "exchange_rate", Number(res.data.rate_in_INR));
      }
    } catch (err) {
      console.error("Failed to fetch exchange rate:", err);
      if (index === "input") {
        updateVendorInputCharge(vendorId, "exchange_rate", "N/A");
      } else {
        handleVendorChargeInputChange(vendorId, index, "exchange_rate", "N/A");
      }
    }
  };

  return {
    vendors,
    setVendors,
    vendorInvoiceDates,
    setVendorInvoiceDates,
    errors,
    setErrors,
    addVendor,
    removeVendor,
    updateVendor,
    updateVendorInputCharge,
    handleVendorChargeInputChange,
    addChargeRow,
    deleteChargeRow,
    handleVendorSelect,
    handleCurrencyChange,
  };
};

export default useVendorState;
